const BASE_URL = process.env.REACT_APP_BASE_URL || "http://192.168.1.5:8001/api/v1";

/* --- TOKEN STORAGE --- */
export const saveTokens = (access, refresh) => {
  sessionStorage.setItem("access_token", access);
  sessionStorage.setItem("refresh_token", refresh);
};

export const clearTokens = () => {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
};

export const getAccessToken = () => sessionStorage.getItem("access_token");
export const getRefreshToken = () => sessionStorage.getItem("refresh_token");

/* --- CORE FETCH WRAPPER --- */
const apiFetch = async (endpoint, options = {}) => {
  const token = getAccessToken();

  // 1. Prepare Headers
  const headers = { ...options.headers };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // 2. Handle Content-Type Automatically
  // If sending FormData (files), let the browser set the boundary header
  if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const config = { ...options, headers };
  const response = await fetch(`${BASE_URL}/${endpoint}`, config);

  // 3. Handle 401 Unauthorized (Token Expiration)
  if (response.status === 401 && endpoint !== "auth/refresh-token") {
    const refreshed = await refreshToken();

    if (refreshed) {
      const newToken = getAccessToken();
      const retryConfig = {
        ...config,
        headers: { ...headers, Authorization: `Bearer ${newToken}` },
      };
      const retryResponse = await fetch(`${BASE_URL}/${endpoint}`, retryConfig);
      return handleResponse(retryResponse);
    }

    // Refresh failed -> Logout
    clearTokens();
    window.location.href = "/admin/login";
    throw new Error("Session expired");
  }

  return handleResponse(response);
};

/* --- RESPONSE HELPER --- */
async function handleResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Request failed" }));
    
    // Check if it's a Pydantic validation error (422)
    if (response.status === 422 && Array.isArray(errorData.detail)) {
      // Create a readable string: "field_name: error_message"
      const readableErrors = errorData.detail
        .map((err) => `${err.loc[err.loc.length - 1]}: ${err.msg}`)
        .join(" | ");
      
      console.error("Validation Details:", errorData.detail);
      throw new Error(readableErrors);
    }

    throw new Error(errorData.detail || "API error");
  }

  const contentType = response.headers.get("content-type");
  return contentType?.includes("application/json") ? response.json() : response.blob();
}

/* --- AUTH APIs --- */
export const LoginService = async ({ username, password }) => {
  const body = new URLSearchParams({ username, password });
  const response = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  const data = await handleResponse(response);
  saveTokens(data.access_token, data.refresh_token);
  return data;
};

export const refreshToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  try {
    const response = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    saveTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false;
  }
};

export const LogoutService = async () => {
  try {
    await apiFetch("auth/logout", { method: "POST" });
  } finally {
    clearTokens();
    window.location.href = "/admin/login";
  }
};

/* =========================
   ADMIN APIs
========================= */

export const CreateEmployeeService = (formData) =>
  apiFetch("admin/create-employee", { method: "POST", body: formData });

export const GetEmployeesService = (skip = 0, limit = 10) =>
  apiFetch(`admin/employees?skip=${skip}&limit=${limit}`);

export const GetOptionsService = (category) => 
  apiFetch(`admin/options?category=${category.toUpperCase()}`);

export const GetEmployeeByIdService = (id) => apiFetch(`admin/employees/${id}`);

export const SearchEmployeesService = (query) =>
  apiFetch(`admin/employees/search?query=${encodeURIComponent(query)}`);

export const GetEmployeeByCodeService = (employee_code) => 
  apiFetch(`admin/employees/code/${employee_code}`);

export const ToggleEmployeeStatusService = (code, active) =>
  apiFetch(`admin/employees/${code}/toggle-status?active=${active}`, { method: "PATCH" });

export const UpdateEmployeeService = (employee_code, data) =>
  apiFetch(`admin/employees/${employee_code}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const UpdatePasswordService = (data) =>
  apiFetch("admin/change-password", {
    method: "PUT",
    body: JSON.stringify(data),
  });


 export const DeleteEmployeeService = (username) =>
  apiFetch(`admin/employees/${username}/full-wipe`, { method: "DELETE" }); 


/* =========================
   EMPLOYEE APIs
========================= */

export const GetMyProfileService = () => apiFetch("employee/me");

export const UpdatePersonalDetailsService = (data) =>
  apiFetch("employee/personal-details", { method: "PATCH", body: JSON.stringify(data) });

export const UpdateKycDetailsService = (data) =>
  apiFetch("employee/kyc-details", { method: "PATCH", body: JSON.stringify(data) });

export const AddEducationService = (educationList) =>
  apiFetch("employee/education", { method: "POST", body: JSON.stringify(educationList) });

export const AddWorkExperienceService = (workList) =>
  apiFetch("employee/work-experience", { method: "POST", body: JSON.stringify(workList) });

export const SyncProfileService = (data) =>
  apiFetch("employee/sync-profile", { method: "POST", body: JSON.stringify(data) });

export const UploadDocumentsService = (formData) =>
  apiFetch("employee/upload-documents", { method: "POST", body: formData });

export const FinalizeOnboardingService = () =>
  apiFetch("employee/finalize-onboarding", { method: "PATCH" });

/* =========================
   DOWNLOAD SERVICES
========================= */

// Download employee profile as PDF
export const AdminDownloadProfilePdfService = async (username) => {
  try {
    const blob = await apiFetch(
      `admin/employees/${username}/download-profile`,
      { 
        method: "GET",
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${username}_profile.pdf`;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};

// ✅ FIXED: Download all employee documents as ZIP
export const DownloadAllfilesService = async (username) => {
  try {
    const blob = await apiFetch(
      `admin/employees/${username}/download-all-files`,  // ✅ Fixed template literal
      {
        method: "GET",
        headers: {
          'Accept': 'application/zip'
        }
      }
    );
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${username}_documents.zip`;  // ✅ Changed to .zip
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Download files error:", error);
    throw error;
  }
};

// Employee downloads their own profile PDF
export const DownloadProfilePdfService = async (employeeId) => {
  try {
    const blob = await apiFetch("employee/download-profile");
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Employee_Profile_${employeeId || "Me"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Download PDF error:", error);
    throw error;
  }
};
